// Shared narration text-builder — turns a V2 agent turn into the FILTERED text
// the voice narrator hears. Extracted from AgentDetail so the ambient-roam
// controller can narrate a background agent it isn't rendering (#1215).
//
// Deliberately FILTERED (Alex 2026-06-24): feed the agent's prose TEXT + a
// structured ACTION SIGNAL per tool (label + status + safe result), but NOT raw
// command output / code / diff bodies. Split into FOREGROUND (the agent's
// message to the operator) and BACKGROUND (tool/file work — "nebensächlich").

import type { V2Turn } from '../api/types';
import { stripAnsi } from '../components/richMarkdown';
import { sanitizeForFeed } from './realtimeVoice';

// FLÜCHTIGE ZÄHLER raus, bevor irgendetwas verglichen wird (Alex, 14.08.:
// „wie ein Radiosprecher, der die Leute bei Laune halten muss — nach 31
// Sekunden ist die Verarbeitung noch nicht fertig, jetzt zwei Minuten…").
//
// Die Ursache war kein gesprächiges Modell, sondern der Zulauf: Laufende
// Werkzeug-Zeilen tragen tickende Uhren („· 9s", „(9s · 4 lines)", „esc to
// interrupt"). Jeder Tick ÄNDERT den Text mitten drin, das Präfix-Delta
// bricht, derselbe Zug wird erneut eingespeist — und das Modell kommentiert
// pflichtbewusst den neuen Zählerstand. Ohne die Uhren ist ein reiner
// Fortschritts-Tick ein LEERES Delta: kein Anlass, kein Wort. Stille, wenn
// nichts Neues passiert, entsteht hier — nicht durch Erziehung des Modells.
export function stripVolatileForNarration(text: string): string {
  return text
    // „(9s · 4 lines)", „(2m 13s)", „(141s)" — Laufzeit-Schmuck der CLIs
    .replace(/\(\s*\d+\s*[ms](?:\s*\d+\s*s)?(?:\s*·\s*[^)]{0,40})?\)/gi, '')
    // „· 141s", „· 2m 13s" als Anhängsel
    .replace(/·\s*\d+\s*[ms](?:\s*\d+\s*s)?\b/gi, '')
    // Bedienhinweise der CLI, die niemand hören muss
    .replace(/\((?:ctrl|esc|cmd)[^)]*\)/gi, '')
    .replace(/\b(?:esc to interrupt|press enter to continue)\b/gi, '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

// Strip code/diagram noise from prose before narration — the voice should hear
// the agent's *explanation*, not raw code/mermaid.
export function stripCodeForNarration(text: string): string {
  return text
    .replace(/```mermaid[\s\S]*?```/gi, ' [Mermaid-Diagramm] ')
    .replace(/```[\s\S]*?```/g, ' [Code-Block] ')
    .replace(/`([^`\n]{25,})`/g, ' [Code] ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Derive a tool outcome — fidelity-safe (Codex 2026-06-24): ONLY an explicitly
// parsed exit code may set success|failed. Everything else stays `unknown` —
// regex-guessing from free output is itself hallucination and is forbidden.
export function deriveToolOutcome(content: string): { status: 'running' | 'success' | 'failed' | 'unknown'; result: string } {
  const c = content || '';
  const exit = c.match(/\bexit(?:\s*code)?[:\s]+(\d+)\b/i) || c.match(/\bexited with(?: code)? (\d+)\b/i);
  if (exit) {
    const n = parseInt(exit[1], 10);
    return { status: n === 0 ? 'success' : 'failed', result: `exit ${n}` };
  }
  return { status: 'unknown', result: '' };
}

// What the agent is DOING — for the voice narrator. FOREGROUND = the agent's
// message/prose to the operator; BACKGROUND = tool/file work. Clean markers
// only; the PROMPT decides to read the message and mention work only marginally.
export function turnNarrationText(turn: V2Turn, opts: { inFlight?: boolean } = {}): string {
  const fg: string[] = []; // message / prose — foreground
  const bg: string[] = []; // tool / file work — background
  const recaps: string[] = [];
  const sections = turn.sections || [];
  for (let i = 0; i < sections.length; i += 1) {
    const s = sections[i];
    if (s.kind === 'text') {
      const t = stripVolatileForNarration(stripCodeForNarration(stripAnsi(s.content || '')));
      if (!t) continue;
      // ZWISCHENANSAGEN sind Arbeit, keine Nachricht (Alex, 13.08.: „ich muss
      // hören, er führt jetzt Airsync aus, er macht jetzt seinen ersten SSH —
      // ich will keine technischen Details"). Agenten schreiben vor jedem
      // Werkzeug-Block eine kurze Ankündigung — „Das prüfe ich:", „Erster
      // Versuch:". Im Portal steht sie zwischen eingeklappten Blöcken und
      // stört nicht; vorgelesen ist sie genau das Prozess-Geplapper, das die
      // Hintergrund-Erzählung längst wegfiltert.
      //
      // Die Erkennung ist STRUKTURELL, keine Wortliste: kurz, endet auf einen
      // Doppelpunkt, und unmittelbar danach folgt ein Arbeits-Block. Eine
      // echte Nachricht erfüllt das nicht — sie steht am Ende des Zuges oder
      // endet als Satz. Wortlisten („führe aus", „kopiere") wären die brüchige
      // Variante derselben Idee und blieben bei jedem neuen Verb stumm falsch.
      const next = sections[i + 1];
      const connectorShape = t.length <= 200 && /[:…]\s*$/.test(t);
      // WÄHREND DES STREAMENS wird ein Konnektor-Kandidat am Zug-Ende
      // ZURÜCKGEHALTEN statt vorläufig eingeordnet. Ohne das kippte die
      // Einordnung nachträglich (erst Nachricht, dann — sobald der Block
      // eintrifft — Arbeit), der Erzähl-Text schrumpfte, das Präfix-Delta
      // brach, und der Zulauf schickte ALLES erneut. Alex hörte jede
      // Aktualisierung zwei- bis dreimal, „ähnlich, aber anders"
      // (13.08.) — das war die Hauptursache.
      if (connectorShape && !next && opts.inFlight) continue;
      const isConnector = connectorShape
        && !!next && next.kind !== 'text' && next.kind !== 'recap' && next.kind !== 'table';
      if (isConnector) { bg.push(t.replace(/[:…]\s*$/, '')); continue; }
      fg.push(t);
    }
    else if (s.kind === 'table') { if (s.markdown) fg.push(s.markdown); }
    else if (s.kind === 'recap') { if (typeof s.summary === 'string' && s.summary) recaps.push(s.summary); }
    else if (s.kind === 'tool') {
      const lbl = sanitizeForFeed((s.label || 'Tool').replace(/\*\*/g, '')).slice(0, 120);
      if (lbl) {
        const { status, result } = deriveToolOutcome(stripAnsi(s.content || ''));
        const res = result ? sanitizeForFeed(result).slice(0, 80) : '';
        bg.push(`${lbl}${status !== 'unknown' ? ` (${status}${res ? ', ' + res : ''})` : ''}`);
      }
    }
    else if (s.kind === 'diff') { const f = s.label ? sanitizeForFeed(s.label.replace(/\*\*/g, '')).slice(0, 80) : ''; bg.push(`Code-Änderung${f ? ' ' + f : ''}`); }
    else if (s.kind === 'tree') bg.push('Verzeichnis angeschaut');
    else if (s.kind === 'tasklist') {
      const items = (s.items || []).map((i) => `${i.done ? '✓' : i.in_progress ? '→' : '○'} ${i.text}`).join('; ');
      if (items) bg.push(`Todo: ${items}`);
    }
    // json / thinking / media → dropped.
  }
  // Background/process narration is OFF by default (Alex 2026-06-24: liked the
  // detail of what the agent SAYS, but the running tool/process commentary was
  // too much + costs tokens). Foreground (agent messages) is ALWAYS kept full.
  // Opt back in via Settings → `cloud-voice-bg='on'`.
  const includeBg = typeof localStorage !== 'undefined' && localStorage.getItem('cloud-voice-bg') === 'on';
  // Der Recap ist eine UMFORMULIERUNG der Prosa davor. Beides einzuspeisen
  // heißt, dasselbe zweimal vorzulesen — Alex' „dann fasst er meistens alles
  // nochmal zusammen" (13.08.). Der Recap zählt deshalb nur, wenn es KEINE
  // Prosa gibt, die er zusammenfassen könnte (reiner Arbeits-Zug).
  if (!fg.length && recaps.length) fg.push(...recaps);
  let out = '';
  if (fg.length) out += `[Nachricht des Agenten an den Operator]\n${fg.join('\n\n')}`;
  if (includeBg && bg.length) out += (out ? '\n\n' : '') + `[Hintergrund-Arbeit]\n${bg.join('; ')}`;
  return out.trim();
}
